import { Instagram, Mail, MapPin, MessageCircle, Phone } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formSubmitUrl = "https://formsubmit.co/khushalipadiya26@gmail.com";

    const formDataObj = new FormData();
    formDataObj.append("name", formData.name);
    formDataObj.append("email", formData.email);
    formDataObj.append("phone", formData.phone);
    formDataObj.append("service", formData.service);
    formDataObj.append("date", formData.date);
    formDataObj.append("message", formData.message);

    // Optional but recommended FormSubmit settings
    formDataObj.append("_captcha", "false"); // disable captcha
    formDataObj.append("_template", "table"); // email layout
    formDataObj.append("_subject", "New Booking Request");
    formDataObj.append("_replyto", formData.email);

    try {
      const response = await fetch(formSubmitUrl, {
        method: "POST",
        body: formDataObj,
      });

      if (response.ok) {
        toast({
          title: "Booking Request Received!",
          description: "Thank you! I'll get back to you soon.",
        });

        // Track conversion in GA
        if (window.gtag) {
          window.gtag('event', 'generate_lead', {
            'event_category': 'Engagement',
            'event_label': 'Booking Form submitted',
            'value': 1.0
          });
        }

        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "",
          date: "",
          message: "",
        });
      } else {
        throw new Error("Form submission failed");
      }
    } catch (error) {
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };


  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-rose-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <p className="text-rose-500 text-sm font-medium tracking-wider uppercase mb-2">Get In Touch</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-800 mb-4">Book Your Appointment</h2>
          <p className="text-stone-600 max-w-2xl mx-auto">Let's make your special day unforgettable</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-semibold text-stone-800 mb-6">Contact Information</h3>
              <div className="space-y-4">
                <Card className="border-rose-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-rose-100 p-3 rounded-full">
                      <Phone className="w-6 h-6 text-rose-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 mb-1">Phone</p>
                      <a href="tel:+919099302024" className="text-stone-600 hover:text-rose-500 transition-colors">
                        +91 90993 02024
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-rose-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-rose-100 p-3 rounded-full">
                      <MessageCircle className="w-6 h-6 text-rose-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 mb-1">WhatsApp</p>
                      <a 
                        href="https://wa.me/919099302024" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-stone-600 hover:text-rose-500 transition-colors"
                        onClick={() => {
                          if (window.gtag) {
                            window.gtag('event', 'whatsapp_click', {
                              'event_category': 'Engagement',
                              'event_label': 'Contact via WhatsApp'
                            });
                          }
                        }}
                      >
                        Chat on WhatsApp
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-rose-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-rose-100 p-3 rounded-full">
                      <Mail className="w-6 h-6 text-rose-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 mb-1">Email</p>
                      <a href="mailto:khushalipadiya26@gmail.com" className="text-stone-600 hover:text-rose-500 transition-colors break-all">
                        khushalipadiya26@gmail.com
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-rose-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-rose-100 p-3 rounded-full">
                      <MapPin className="w-6 h-6 text-rose-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 mb-1">Location</p>
                      <p className="text-stone-600">Pune, Hinjewadi 411057</p>
                      <p className="text-sm text-rose-500 mt-1">Travel worldwide or client location</p>
                      <p className="text-xs text-stone-500">(Transportation by client)</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="bg-rose-100 rounded-2xl p-6">
              <h4 className="font-semibold text-stone-800 mb-4">Follow Me</h4>
              <div className="flex items-center space-x-4">
                <a href="https://www.instagram.com/khushi.mua/" className="bg-white p-3 rounded-full hover:bg-rose-500 hover:text-white transition-all duration-300 group">
                  <Instagram className="w-6 h-6 text-rose-500 group-hover:text-white" />
                </a>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <Card className="border-rose-200 shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-semibold text-stone-800 mb-6">Send a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
                    placeholder="Your Name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Service Required *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
                  >
                    <option value="">Select a service</option>
                    <option value="Bridal HD Makeup">Bridal HD Makeup</option>
                    <option value="Bridal Airbrush Makeup">Bridal Airbrush Makeup</option>
                    <option value="Bridesmaids Makeup">Bridesmaids Makeup</option>
                    <option value="Baby Shower Makeup">Baby Shower Makeup</option>
                    <option value="Photoshoot Makeup">Photoshoot Makeup</option>
                    <option value="Floral Jewellery">Floral Jewellery</option>
                    <option value="Pre-Bridal Services">Pre-Bridal Services</option>
                    <option value="Bridal Outfit Styling">Bridal Outfit Styling</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Preferred Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all resize-none"
                    placeholder="Tell me about your requirements..."
                  ></textarea>
                </div>

                <Button type="submit" className="w-full bg-rose-500 hover:bg-rose-600 text-white py-6 text-lg transition-all duration-300 hover:scale-105">
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;